import React, { useEffect, useState } from 'react';
import { sb } from '../../supabase';

// Set or clear the response deadline on one RFQ.
//
// This is the field that gates every share: the public RFQ page hides the
// "Respond by" row when it is null, and the editorial rule is that no link
// goes out without one. Until 2026-09-19 the console could not write it at
// all — there is no admin UPDATE policy on public.requests, so a plain
// .update() matched zero rows and reported success. It goes through
// admin_set_request_deadline (20260918222742), which can actually refuse.
//
// The RPC returns the STORED value and this component renders that, not what
// it sent. A save that silently did nothing would otherwise look identical to
// one that worked, which is the whole reason the RPC exists.

// <input type="date"> speaks 'YYYY-MM-DD' in LOCAL time. Converting through
// toISOString() would shift the date backwards for anyone east of UTC — which
// is everyone here — so the parts are assembled by hand in both directions.
const toInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// End of the chosen day, local time: a deadline of "26 Sept" means suppliers
// have all of the 26th, not until 00:00 on it.
const toTimestamp = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59).toISOString();
};

const todayValue = () => toInputValue(new Date().toISOString());

export default function DeadlineEditor({ requestId, deadline, isAr, flash, onSaved }) {
  const [value, setValue] = useState(toInputValue(deadline));
  const [saved, setSaved] = useState(deadline || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Re-sync when the parent reloads the request under us.
  useEffect(() => {
    setValue(toInputValue(deadline));
    setSaved(deadline || null);
  }, [deadline]);

  const dirty = toInputValue(saved) !== value;
  // Warned, not blocked — the database deliberately accepts past dates so an
  // admin can correct or backdate one (owner, 2026-09-19).
  const isPast = Boolean(value) && value < todayValue();

  const save = async (next) => {
    setErr('');
    setBusy(true);
    const { data, error } = await sb.rpc('admin_set_request_deadline', {
      p_request_id: requestId,
      p_deadline: next,
    });
    setBusy(false);

    if (error) {
      console.error('[DeadlineEditor] admin_set_request_deadline failed:', error.code || '', error.message || error);
      setErr(isAr ? 'تعذّر الحفظ.' : 'Could not save.');
      return;
    }

    // Render what the database actually holds, not what we sent.
    setSaved(data || null);
    setValue(toInputValue(data));
    flash?.(next
      ? (isAr ? 'حُفظ الموعد.' : 'Deadline saved.')
      : (isAr ? 'أُزيل الموعد.' : 'Deadline cleared.'));
    onSaved?.(data || null);
  };

  return (
    <div className="ac-card" style={{ padding: 20 }}>
      <strong style={{ fontSize: 14 }}>
        {isAr ? 'آخر موعد للرد' : 'Response deadline'}
      </strong>

      <p style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--ac-muted)', margin: '10px 0 14px' }}>
        {isAr
          ? 'يظهر للمورّد في صفحة الطلب العامة. بلا موعد لا يظهر السطر إطلاقًا — ولا يُنشر رابط بلا موعد. الموعد للضغط فقط: مروره لا يغلق الطلب، الإغلاق يدوي.'
          : 'Shown to suppliers on the public request page. With no date the row is hidden entirely — and no link gets shared without one. The date is pressure only: passing it does not close the request, closing is manual.'}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="date"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          style={{
            padding: '9px 11px',
            borderRadius: 8,
            border: '1px solid var(--ac-line, rgba(0,0,0,.12))',
            background: 'var(--ac-bg, #fff)',
            fontSize: 14,
          }}
        />
        <button
          className="ac-btn ac-btn-sm ac-btn-primary"
          onClick={() => save(toTimestamp(value))}
          disabled={busy || !value || !dirty}
        >
          {busy ? '…' : (isAr ? 'حفظ' : 'Save')}
        </button>
        {saved && (
          <button className="ac-btn ac-btn-sm" onClick={() => save(null)} disabled={busy}>
            {isAr ? 'إزالة' : 'Clear'}
          </button>
        )}
      </div>

      {isPast && (
        <p style={{ fontSize: 12, color: 'var(--ac-warn, #b7791f)', margin: '10px 0 0', lineHeight: 1.7 }}>
          {isAr
            ? '⚠ هذا التاريخ في الماضي. سيُحفظ كما هو، لكن المورّد سيرى موعدًا منقضيًا على صفحة عامة.'
            : '⚠ That date is in the past. It will save, but a supplier will see an expired deadline on a public page.'}
        </p>
      )}

      {!saved && (
        <p style={{ fontSize: 12, color: 'var(--ac-muted)', margin: '10px 0 0' }}>
          {isAr ? 'لا يوجد موعد — لا تنشر رابطًا لهذا الطلب بعد.' : 'No deadline set — do not share a link for this request yet.'}
        </p>
      )}

      {err && <p style={{ fontSize: 13, color: 'var(--ac-danger, #c0392b)', margin: '10px 0 0' }}>{err}</p>}
    </div>
  );
}
